import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { useSubscription } from '../hooks/useSubscription';
import {
  getAttributionSnapshot,
  isPurchaseEventSent,
  markPurchaseEventSent,
} from '../lib/attribution';
import { trackPurchase } from '../lib/analytics/gtag';

const REFRESH_DELAY_MS = 2500;
const API_BASE = process.env.REACT_APP_API_URL || '';

async function fetchEntitlementActive() {
  if (!API_BASE) return false;
  const user = getAuth().currentUser;
  if (!user) return false;
  try {
    const token = await user.getIdToken();
    const res = await fetch(`${API_BASE}/api/account/entitlement`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return false;
    const data = await res.json();
    return data.active === true;
  } catch {
    return false;
  }
}

async function fetchOrderStatus(orderId) {
  if (!API_BASE || !orderId) return null;
  const user = getAuth().currentUser;
  if (!user) return null;
  try {
    const token = await user.getIdToken();
    const res = await fetch(`${API_BASE}/api/payments/orders/${encodeURIComponent(orderId)}/status`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

async function markPurchaseSentOnServer(orderId) {
  if (!API_BASE || !orderId) return false;
  const user = getAuth().currentUser;
  if (!user) return false;
  try {
    const token = await user.getIdToken();
    const res = await fetch(
      `${API_BASE}/api/payments/orders/${encodeURIComponent(orderId)}/analytics/purchase-sent`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return res.ok;
  } catch {
    return false;
  }
}

function resolveTransactionId(orderStatus, orderId) {
  if (!orderStatus) return orderId || undefined;
  return (
    orderStatus.provider_capture_id ||
    orderStatus.provider_reference ||
    orderStatus.payfast_payment_id ||
    orderStatus.order_id ||
    orderId
  );
}

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshToken } = useSubscription();
  const [status, setStatus] = useState('idle');
  const [backendActive, setBackendActive] = useState(false);
  const [orderStatus, setOrderStatus] = useState(null);
  const purchaseTrackedRef = useRef(false);
  const orderId = searchParams.get('order_id');
  const provider = searchParams.get('provider');

  useEffect(() => {
    const t = setTimeout(async () => {
      setStatus('refreshing');
      try {
        await refreshToken();
        const active = await fetchEntitlementActive();
        setBackendActive(active);
        let resolvedOrderStatus = null;
        if (orderId) {
          resolvedOrderStatus = await fetchOrderStatus(orderId);
          setOrderStatus(resolvedOrderStatus);
          if (!active && resolvedOrderStatus?.access_granted) {
            setBackendActive(true);
          }
        } else if (active) {
          setBackendActive(true);
        }
        setStatus('refreshed');
      } catch (err) {
        console.error('Payment success token refresh failed:', err);
        setStatus('error');
      }
    }, REFRESH_DELAY_MS);
    return () => clearTimeout(t);
  }, [refreshToken, orderId]);

  useEffect(() => {
    if (status !== 'refreshed' || purchaseTrackedRef.current) return;

    const accessVerified =
      backendActive || orderStatus?.access_granted === true;
    if (!accessVerified) return;

    const effectiveOrderId = orderId || orderStatus?.order_id;
    if (!effectiveOrderId) return;

    if (orderStatus?.analytics_purchase_sent_at || isPurchaseEventSent(effectiveOrderId)) {
      purchaseTrackedRef.current = true;
      return;
    }

    const transactionId = resolveTransactionId(orderStatus, effectiveOrderId);
    const value = orderStatus?.amount_usd;
    if (value == null && !orderStatus) return;

    purchaseTrackedRef.current = true;
    markPurchaseEventSent(effectiveOrderId);

    trackPurchase({
      transactionId,
      value: value ?? 0,
      currency: orderStatus?.currency || 'USD',
      planId: orderStatus?.plan_id,
      planName: orderStatus?.item_name,
      paymentProvider: orderStatus?.provider || provider || undefined,
      attributionSnapshot: orderStatus?.attribution || getAttributionSnapshot(),
    });

    markPurchaseSentOnServer(effectiveOrderId).catch(() => {});
  }, [status, backendActive, orderStatus, orderId, provider]);

  if (status === 'idle' || status === 'refreshing') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-green-50 px-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-blue-500 text-5xl mb-4 animate-pulse">⟳</div>
          <h1 className="text-2xl font-bold mb-2">Payment successful</h1>
          <p className="text-gray-700 mb-6">Confirming payment…</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-amber-50 px-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-amber-500 text-5xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold mb-2">Payment received</h1>
          <p className="text-gray-700 mb-6">
            If your access doesn&apos;t update in a few seconds, refresh the page or go to your account.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
              onClick={async () => {
                setStatus('refreshing');
                try {
                  await refreshToken();
                  setStatus('refreshed');
                } catch {
                  setStatus('error');
                }
              }}
            >
              Refresh your access
            </button>
            <button
              className="px-6 py-3 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition"
              onClick={() => navigate('/account')}
            >
              Go to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-green-50 px-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
        <div className="text-green-500 text-5xl mb-4">✔️</div>
        <h1 className="text-2xl font-bold mb-2">Payment successful</h1>
        <p className="text-gray-700 mb-6">
          {backendActive
            ? 'Your Cognidoor access has been activated.'
            : 'Thank you for your payment. If your access is not active yet, refresh this page or go to your account.'}
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <button
            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
            onClick={() => navigate('/account')}
          >
            Go to dashboard
          </button>
          <button
            className="px-6 py-3 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition"
            onClick={() => navigate('/')}
          >
            Return to Home
          </button>
        </div>
      </div>
    </div>
  );
}
